import { create } from 'zustand'
import {
  advanceCursor,
  createCursorState,
  goBack,
  type CursorState,
} from '../components/list-system/cursor-model'

export type MessagingDraftSurface = 'admin' | 'seller' | 'supply'
export type AdminInboxMobilePanel = 'list' | 'timeline' | 'context'
export type TenantMessagingDrawerView = 'list' | 'thread'

function draftKey(surface: MessagingDraftSurface, conversationId: string) {
  return `${surface}:${conversationId}`
}

type MessagingUiStore = {
  activeConversationId: string | null
  adminSelectionScope: string | null
  drafts: Record<string, string>
  adminCursorState: CursorState
  adminScrollPositions: Record<string, number>
  adminMobilePanel: AdminInboxMobilePanel
  tenantDrawerOpen: boolean
  tenantDrawerView: TenantMessagingDrawerView
  setActiveConversationId: (conversationId: string | null) => void
  selectAdminConversation: (conversationId: string, scope: string) => void
  clearAdminSelection: () => void
  getDraft: (surface: MessagingDraftSurface, conversationId: string) => string
  setDraft: (
    surface: MessagingDraftSurface,
    conversationId: string,
    value: string,
  ) => void
  clearDraft: (surface: MessagingDraftSurface, conversationId: string) => void
  advanceAdminCursor: (cursor: string) => void
  goBackAdminCursor: () => void
  resetAdminCursor: (current?: string | null) => void
  reconcileAdminCursor: (current: string | null) => void
  setAdminScrollPosition: (key: string, position: number) => void
  setAdminMobilePanel: (panel: AdminInboxMobilePanel) => void
  openAdminConversation: (conversationId: string, scope: string) => void
  openAdminContext: () => void
  backAdminMobilePanel: () => void
  openTenantDrawer: (conversationId?: string) => void
  closeTenantDrawer: () => void
  showTenantConversationList: () => void
  reset: () => void
}

export const useMessagingUiStore = create<MessagingUiStore>((set, get) => ({
  activeConversationId: null,
  adminSelectionScope: null,
  drafts: {},
  adminCursorState: createCursorState(),
  adminScrollPositions: {},
  adminMobilePanel: 'list',
  tenantDrawerOpen: false,
  tenantDrawerView: 'list',
  setActiveConversationId: (activeConversationId) => set({ activeConversationId }),
  selectAdminConversation: (activeConversationId, adminSelectionScope) =>
    set({ activeConversationId, adminSelectionScope }),
  clearAdminSelection: () =>
    set({ activeConversationId: null, adminSelectionScope: null }),
  getDraft: (surface, conversationId) =>
    get().drafts[draftKey(surface, conversationId)] ?? '',
  setDraft: (surface, conversationId, value) =>
    set((state) => ({
      drafts: {
        ...state.drafts,
        [draftKey(surface, conversationId)]: value,
      },
    })),
  clearDraft: (surface, conversationId) =>
    set((state) => {
      const drafts = { ...state.drafts }
      delete drafts[draftKey(surface, conversationId)]
      return { drafts }
    }),
  advanceAdminCursor: (cursor) =>
    set((state) => ({
      adminCursorState: advanceCursor(state.adminCursorState, cursor),
    })),
  goBackAdminCursor: () =>
    set((state) => ({ adminCursorState: goBack(state.adminCursorState) })),
  resetAdminCursor: (current = null) =>
    set({ adminCursorState: { current, previous: [] } }),
  reconcileAdminCursor: (current) =>
    set((state) => {
      if (state.adminCursorState.current === current) return state
      if (state.adminCursorState.previous.at(-1) === current) {
        return { adminCursorState: goBack(state.adminCursorState) }
      }
      return { adminCursorState: { current, previous: [] } }
    }),
  setAdminScrollPosition: (key, position) =>
    set((state) => ({
      adminScrollPositions: { ...state.adminScrollPositions, [key]: position },
    })),
  setAdminMobilePanel: (adminMobilePanel) => set({ adminMobilePanel }),
  openAdminConversation: (activeConversationId, adminSelectionScope) =>
    set({
      activeConversationId,
      adminSelectionScope,
      adminMobilePanel: 'timeline',
    }),
  openAdminContext: () =>
    set((state) =>
      state.activeConversationId ? { adminMobilePanel: 'context' } : state,
    ),
  backAdminMobilePanel: () =>
    set((state) => ({
      adminMobilePanel:
        state.adminMobilePanel === 'context' ? 'timeline' : 'list',
    })),
  openTenantDrawer: (conversationId) =>
    set({
      tenantDrawerOpen: true,
      tenantDrawerView: conversationId ? 'thread' : 'list',
      ...(conversationId ? { activeConversationId: conversationId } : {}),
    }),
  closeTenantDrawer: () => set({ tenantDrawerOpen: false }),
  showTenantConversationList: () => set({ tenantDrawerView: 'list' }),
  reset: () =>
    set({
      activeConversationId: null,
      adminSelectionScope: null,
      drafts: {},
      adminCursorState: createCursorState(),
      adminScrollPositions: {},
      adminMobilePanel: 'list',
      tenantDrawerOpen: false,
      tenantDrawerView: 'list',
    }),
}))
